import { E_LEARNING_FIELDS, eLearningMapper } from '../mappers/e-learning.mapper';
import { exportExcelMapper } from '../mappers/export-excel.mapper';
import { managerRequestMapper } from '../mappers/group-admin-request.mapper';
import { laminatedCardApplePayMapper } from '../mappers/laminated-card-apple-pay.mapper';
import { laminatedCardPaymentMapper } from '../mappers/laminated-card-payment.mapper';
import { learnerPositionMapper } from '../mappers/learner-position.mapper';
import { paginationInfoMapper } from '../mappers/pagination-info.mapper';
import { queryParamsMapper } from '../mappers/query-params.mapper';
import { saveCurrentPositionDataMapper } from '../mappers/save-current-position-data.mapper';
import { Course } from '../models/course';
import { ELearning, ELearningDetail } from '../models/e-learning';
import { ExportExcel } from '../models/export-excel';
import { LaminatedCardApplePayCreation } from '../models/laminated-card-apple-pay';
import { LaminatedCardPaymentCreation } from '../models/laminated-card-payment';
import { Learner, LearnerWithInstructorNotes } from '../models/learner';
import { LearnerPosition } from '../models/learner-position';
import { Order } from '../models/order';
import { Package } from '../models/package';
import { PaginationInfo } from '../models/pagination-info';
import { QueryParams } from '../models/query-params';
import { SaveCurrentPositionData } from '../models/save-current-position-data';
import { User } from '../models/user';
import { downloadFileService } from '../ui-services/download-file.service';
import { appUrlsConfig } from './app-urls.config';
import { axiosService, cancellableGet, catchHttpError } from './axios.service';
import { TableQueryParams, prepareTableQueryParams } from './table-query-params.service';

export class ELearningRegistrationOptions extends TableQueryParams {
  constructor(data) {
    super(data);

    /** @type {User} */
    this.groupAdmin = data.groupAdmin;

    /** @type {User | undefined} */
    this.learner = data.learner;
  }
}

export class EmailLearnersOptions {
  constructor(data) {
    /** @type {string} */
    this.subject = data.subject;

    /** @type {string} */
    this.body = data.body;

    /** @type {number[]} */
    this.registrationIds = data.registrationIds;
  }
}

/**
 * Prepare e-learning registration options.
 * @param {ELearningRegistrationOptions} options Options.
 * @returns {object}
 */
function prepareELearningRegistrationOptions(options) {
  return {
    ...prepareTableQueryParams({ options, sortField: E_LEARNING_FIELDS }),
    ...managerRequestMapper.toDto({ groupAdmin: options.groupAdmin }),
    user_id: options?.learner?.id,
  };
}

/**
 * Get e-learning assigned courses.
 * @param {ELearningRegistrationOptions} options Options.
 * @returns {Promise<PaginationInfo>}.
 */
async function getAssignedCourses(options) {
  const response = await cancellableGet(appUrlsConfig.eLearning.getLearningRegistrations, {
    params: prepareELearningRegistrationOptions(options),
  });
  return paginationInfoMapper.fromDto(response.data, eLearningMapper.fromDto);
}

/**
 * Get e-learning assigned courses for admin extension.
 * @param {ELearningRegistrationOptions} options Options.
 * @returns {Promise<PaginationInfo>}.
 */
async function getExtendedAssignedCourses(options) {
  const response = await cancellableGet(appUrlsConfig.eLearning.extendedList, {
    params: prepareELearningRegistrationOptions(options),
  });
  return paginationInfoMapper.fromDto(response.data, eLearningMapper.fromDto);
}

/**
 * Get e-learning course detail.
 * @param {number} id Id.
 * @param {boolean | null} isIncludedLaminatedCard Is included laminated card.
 * @returns {Promise<ELearningDetail>}.
 */
async function getDetail(id, isIncludedLaminatedCard = null) {
  const response = await axiosService.get(appUrlsConfig.eLearning.getDetail(id), {
    params: eLearningMapper.toIncludeLaminatedCardIncludeInfoDto(isIncludedLaminatedCard),
  });
  return eLearningMapper.fromDetailDto(response.data);
}

/**
 * Assign e-learning courses to learners.
 * @param {object} data Assign courses data.
 * @param {LearnerWithInstructorNotes[] | Learner[]} data.learners Learners.
 * @param {Package} data.selectedPackage Selected package.
 * @param {User} data.groupAdmin Group admin.
 * @param {number} data.divisionId Division ID.
 * @returns {Promise<void>}.
 */
async function assignELearningCourses({
  learners,
  selectedPackage,
  groupAdmin,
  divisionId,
}) {
  return catchHttpError(async () => {
    const response = await axiosService.post(
      appUrlsConfig.eLearning.assignELearningCourses(selectedPackage.id),
      eLearningMapper.toAssignCoursesDto({ learners, groupAdmin, divisionId }),
    );
    return response.data;
  });
}

/**
 * Assign e-learning courses to learners with learner IDs.
 * @param {object} data Assign courses data.
 * @param {number[]} data.learnerIds Learners.
 * @param {Package} data.selectedPackage Selected package.
 * @param {User} data.groupAdmin Group admin.
 * @returns {Promise<void>}.
 */
async function assignELearningCoursesWithIds({
  learnerIds,
  selectedPackage,
  groupAdmin,
}) {
  return catchHttpError(async () => {
    const response = await axiosService.post(
      appUrlsConfig.eLearning.assignELearningCourses(selectedPackage.id),
      eLearningMapper.toAssignCoursesWithLearnerIdsDto({ learnerIds, groupAdmin }),
    );
    return response.data;
  });
}

/**
 * Restart training.
 * @param {ELearning} eLearning E-learning.
 * @returns {Promise<{url: string | null}>}.
 */
async function restartTraining(eLearning) {
  return catchHttpError(async () => {
    const response = await axiosService.put(
      appUrlsConfig.eLearning.restartTraining,
      eLearningMapper.toRestartTrainingDto(eLearning),
    );
    return response.data;
  });
}

/**
 * Get learner positions.
 * @param {number} registrationId Registration id.
 * @returns {Promise<LearnerPosition[]>}.
 */
async function getLearnerPositions(registrationId) {
  const response = await axiosService.get(appUrlsConfig.eLearning.learnerPositions(registrationId));
  return response.data.map(dto => learnerPositionMapper.fromDto(dto));
}

/**
 * Update learner position.
 * @param {object} data Update learner position data.
 * @param {number} data.registrationId Registration id.
 * @param {number} data.learnerPositionId Learner position id.
 * @returns {Promise<void>}.
 */
async function updateLearnerPosition({ registrationId, learnerPositionId }) {
  await axiosService.put(appUrlsConfig.eLearning.updateLearnerPositions(registrationId, learnerPositionId));
}

/**
 * Update learner progress.
 * @param {object} data Update learner position data.
 * @param {number} data.registrationId Registration id.
 * @param {number} data.progress Progress.
 * @returns {Promise<void>}.
 */
async function updateLearnerProgress({ registrationId, progress }) {
  const response = await axiosService.put(
    appUrlsConfig.eLearning.updateLearnerProgress(registrationId),
    eLearningMapper.toUpdateProgressDto(progress),
  );
  return response.data;
}

/**
 * Email certificate for learner.
 * @param {number[]} registrationIds Registration ids.
 * @returns {Promise<void>}.
 */
async function emailCertificate(registrationIds) {
  return catchHttpError(async () => {
    const response = await axiosService.post(
      appUrlsConfig.eLearning.emailCertificate,
      eLearningMapper.toRegistrationIdsDto(registrationIds),
    );
    return response.data;
  });
}

/**
 * Resend password to learner.
 * @param {number[]} registrationIds Registration id.
 * @returns {Promise<void>}.
 */
async function resendPassword(registrationIds) {
  const response = await axiosService.post(
    appUrlsConfig.eLearning.resendPassword,
    eLearningMapper.toRegistrationIdsDto(registrationIds),
  );
  return response.data;
}

/**
 * Export e-learning excel.
 * @param {object} data Query params.
 * @param {QueryParams} data.queryParams Query params.
 * @param {number[]} data.selectedRegistrationIds Selected registration ids.
 * @param {User} data.groupAdmin Group admin.
 * @param {User} data.learner Group admin.
 * @returns {Promise<ExportExcel>}.
 */
async function exportReport({
  queryParams, selectedRegistrationIds, groupAdmin, learner,
}) {
  const response = await axiosService.get(appUrlsConfig.eLearning.export, {
    params: exportExcelMapper.toDto({
      queryParams: queryParamsMapper.toDto(queryParams, E_LEARNING_FIELDS),
      selectedRegistrationIds,
      groupAdmin,
      learner,
    }),
  });

  return exportExcelMapper.fromDto(response.data);
}

/**
 * Update language.
 * @param {number} registrationId Registration id.
 * @param {number} languageId Registration id.
 * @returns {Promise<void>}.
 */
async function updateLanguage(registrationId, languageId) {
  const response = await axiosService.put(appUrlsConfig.eLearning.updateLanguage(registrationId, languageId));
  return response.data;
}

/**
 * Email to learners.
 * @param {EmailLearnersOptions} options Options.
 * @returns {Promise<void>}.
 */
async function emailLearners(options) {
  const email = {
    ...options,
    ...eLearningMapper.toRegistrationIdsDto(options.registrationIds),
  };

  const response = await axiosService.post(
    appUrlsConfig.eLearning.emailLearners,
    email,
  );
  return response.data;
}

/**
 * Download certificates as zip.
 * @param {number[]} registrationIds Registration ids.
 * @param {object} userInfo User info.
 * @returns {Promise<object>}.
 */
async function downloadCertificatesAsZip(registrationIds, userInfo = null) {
  return catchHttpError(async () => {
    const response = await axiosService.post(
      appUrlsConfig.eLearning.downloadCertificatesAsZip,
      eLearningMapper.toRegistrationIdsDto(registrationIds, userInfo),
      {
        responseType: 'blob',
      },
    );

    if (response) {
      downloadFileService.streamDownloadCertificatesAsZip(response);
    }
  });
}

/**
 * Download certificates.
 * @param {number[]} registrationIds Registration ids.
 * @returns {Promise<object>}.
 */
async function downloadCertificates(registrationIds) {
  return catchHttpError(async () => {
    const response = await axiosService.get(appUrlsConfig.eLearning.downloadCertificates, {
      params: eLearningMapper.toRegistrationIdsDto(registrationIds),
    });

    return eLearningMapper.mapDownloadedCertificatesFromDto(response.data);
  });
}

/**
 * Hide courses.
 * @param {number[]} registrationIds Registration ids.
 * @returns {Promise<object>}.
 */
async function hideCourses(registrationIds) {
  await axiosService.put(
    appUrlsConfig.eLearning.hideCourses,
    eLearningMapper.toRegistrationIdsDto(registrationIds),
  );
}

/**
 * Unhide courses.
 * @param {number[]} registrationIds Registration ids.
 * @returns {Promise<object>}.
 */
async function unhideCourses(registrationIds) {
  await axiosService.put(
    appUrlsConfig.eLearning.unhideCourses,
    eLearningMapper.toRegistrationIdsDto(registrationIds),
  );
}

/**
 * Transfer course.
 * @param {Learner} learner Learner.
 * @param {Course} course Course.
 * @returns {Promise<{url: string | null}>}.
 */
async function transferCourse(learner, course) {
  return catchHttpError(async () => {
    const response = await axiosService.put(
      appUrlsConfig.eLearning.transferCourse(course.registration.id),
      eLearningMapper.toTransferCourseDto(learner, course),
    );

    return response.data;
  });
}

/**
 * Return license to customer available licenses.
 * @param {number} registrationId Registration id.
 * @returns {Promise<void>}.
 */
async function returnLicense(registrationId) {
  return catchHttpError(async () => {
    await axiosService.put(appUrlsConfig.eLearning.returnLicense(registrationId));
  });
}

/**
 * Update general settings.
 * @param {ELearning} eLearning E-learning.
 * @param {object} settings Settings.
 * @returns {Promise<void>}.
 */
async function updateGeneralSettings(eLearning, settings) {
  return catchHttpError(async () => {
    const response = await axiosService.put(
      appUrlsConfig.eLearning.generalSettings(eLearning.id),
      eLearningMapper.toGeneralSettingsDto(eLearning, settings),
    );

    return response.data;
  });
}

/**
 * Update expired date.
 * @param {ELearning} eLearning E-learning.
 * @param {object} settings Settings.
 * @returns {Promise<void>}.
 */
async function updateExpiredDate(eLearning, settings) {
  return catchHttpError(async () => {
    const response = await axiosService.put(
      appUrlsConfig.eLearning.generalSettings(eLearning.id),
      eLearningMapper.toExpirationDateDto(settings),
    );

    return response.data;
  });
}

/**
 * Batch extend expiration date.
 * @param {object} data Payload for batch extend request.
 * @param {number[]} data.registrationIds Registration ids.
 * @param {string} data.expirationDate Expiration date (YYYY-MM-DD).
 * @returns {Promise<void>}.
 */
async function batchExtend({ registrationIds, expirationDate }) {
  await axiosService.post(appUrlsConfig.eLearning.batchExtend, {
    registration_ids: registrationIds,
    expiration_date: expirationDate,
  });
}

/**
 * Create order.
 * @param {number} registrationId Registration ID.
 * @param {LaminatedCardPaymentCreation} paymentInfo Laminated card payment info.
 * @returns {Promise<ELearning>}
 */
async function payLaminatedCard(registrationId, paymentInfo) {
  return catchHttpError(async () => {
    const dataDto = laminatedCardPaymentMapper.toCreationDto(paymentInfo);
    const response = await axiosService.post(appUrlsConfig.eLearning.payLaminatedCard(registrationId), dataDto);
    return response.data;
  });
}

/**
 * Pay laminated card via Apple Pay.
 * @param {number} registrationId Registration ID.
 * @param {LaminatedCardApplePayCreation} paymentInfo Apple Pay payment info.
 * @returns {Promise<ELearning>}
 */
async function payLaminatedCardViaApplePay(registrationId, paymentInfo) {
  return catchHttpError(async () => {
    const dataDto = laminatedCardApplePayMapper.toCreationDto(paymentInfo);
    const response = await axiosService.post(appUrlsConfig.eLearning.payLaminatedCard(registrationId), dataDto);
    return response.data;
  });
}

/**
 * Pay laminated card via PayPal.
 * @param {number} registrationId Registration ID.
 * @param {{ paypal_order_id: string }} payload PayPal order payload.
 * @returns {Promise<ELearning>}
 */
async function payLaminatedCardViaPayPal(registrationId, payload) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.eLearning.payLaminatedCard(registrationId), payload);
    return response.data;
  });
}

/**
 * Save current position.
 * @param {number} registrationId Registration ID.
 * @param {SaveCurrentPositionData} data Save current position data.
 * @returns {Promise<Order>}
 */
async function saveCurrentPosition(registrationId, data) {
  return catchHttpError(async () => {
    const dataDto = saveCurrentPositionDataMapper.toCreationDto(data);
    const response = await axiosService.put(appUrlsConfig.eLearning.saveCurrentPosition(registrationId), dataDto);
    return response.data;
  });
}

export const eLearningService = {
  getAssignedCourses,
  assignELearningCourses,
  restartTraining,
  getLearnerPositions,
  updateLearnerPosition,
  updateLearnerProgress,
  emailCertificate,
  resendPassword,
  exportReport,
  getDetail,
  updateLanguage,
  emailLearners,
  downloadCertificates,
  downloadCertificatesAsZip,
  hideCourses,
  unhideCourses,
  transferCourse,
  returnLicense,
  updateGeneralSettings,
  updateExpiredDate,
  batchExtend,
  payLaminatedCard,
  payLaminatedCardViaApplePay,
  payLaminatedCardViaPayPal,
  saveCurrentPosition,
  assignELearningCoursesWithIds,
  getExtendedAssignedCourses,
};
